import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Bell,
  Sun,
  Moon,
  ChevronDown,
  Menu,
  LogOut,
  ChevronRight,
} from "lucide-react";


const breadcrumbMap = {
  overview: ["Overview"],
  projectList: ["Projects", "Project List"],
  project: ["Projects", "Create Project"],
  projectDetail: ["Projects", "Project Detail"],
  mathLibrary: ["Study", "Math", "Math Library"],
  mathCourses: ["Study", "Math", "Math Courses"],
  mathAccess: ["Study", "Math", "Math Access"],
  theologyAccess: ["Study", "Theology", "Theology Access"],
  csDataSciencePassword: ["Study", "CS", "Data Science Password"],
  spanishExamPassword: ["Study", "Spanish", "Exam Password"],
  settings: ["Settings"],
  operationPassword: ["Study", "CS", "Data Science Password"],
};

function useDarkMode() {
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains("dark")
  );
  const toggle = () => {
    const html = document.documentElement;
    if (html.classList.contains("dark")) {
      html.classList.remove("dark");
      setDark(false);
      localStorage.setItem("theme", "light");
    } else {
      html.classList.add("dark");
      setDark(true);
      localStorage.setItem("theme", "dark");
    }
  };
  return [dark, toggle];
}

export default function AdminTopbar({
  viewMode,
  collapsed,
  setCollapsed,
  onMobileMenuOpen,
  user,
  onLogout,
}) {
  const [dark, toggleDark] = useDarkMode();
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const profileRef = useRef(null);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const crumbs = breadcrumbMap[viewMode] || [viewMode];

  return (
    <header className="admin-topbar">
      {/* Left: collapse toggle + breadcrumbs */}
      <div className="admin-topbar-left">
        {/* Desktop collapse toggle */}
        <motion.button
          whileTap={{ scale: 0.93 }}
          onClick={() => setCollapsed((c) => !c)}
          className="admin-topbar-icon-btn admin-topbar-collapse-btn"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <Menu size={18} />
        </motion.button>

        {/* Mobile hamburger */}
        <motion.button
          whileTap={{ scale: 0.93 }}
          onClick={onMobileMenuOpen}
          className="admin-topbar-icon-btn admin-topbar-mobile-menu"
          title="Open navigation"
        >
          <Menu size={18} />
        </motion.button>

        {/* Breadcrumbs */}
        <nav className="admin-breadcrumbs" aria-label="Breadcrumb">
          <span className="admin-breadcrumb-root">Admin</span>
          {crumbs.map((crumb, i) => (
            <React.Fragment key={crumb}>
              <ChevronRight size={13} className="admin-breadcrumb-sep" />
              <span
                className={`admin-breadcrumb-item ${i === crumbs.length - 1 ? "admin-breadcrumb-active" : ""}`}
              >
                {crumb}
              </span>
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* Right: search + actions */}
      <div className="admin-topbar-right">
        {/* Search */}
        <div className={`admin-search ${searchFocused ? "admin-search--focused" : ""}`}>
          <Search size={14} className="admin-search-icon" />
          <input
            type="text"
            placeholder="Search..."
            className="admin-search-input"
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
        </div>

        {/* Notification Bell */}
        <motion.button
          whileTap={{ scale: 0.93 }}
          className="admin-topbar-icon-btn admin-topbar-bell"
          title="Notifications"
        >
          <Bell size={17} />
          <span className="admin-notif-dot" />
        </motion.button>

        {/* Theme Toggle */}
        <motion.button
          whileTap={{ scale: 0.93 }}
          onClick={toggleDark}
          className="admin-topbar-icon-btn"
          title={dark ? "Switch to light mode" : "Switch to dark mode"}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={dark ? "moon" : "sun"}
              initial={{ rotate: -45, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 45, opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{ display: "flex" }}
            >
              {dark ? <Sun size={17} /> : <Moon size={17} />}
            </motion.span>
          </AnimatePresence>
        </motion.button>

        {/* User Profile Dropdown */}
        <div className="admin-profile-wrap" ref={profileRef}>
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={() => setProfileOpen((o) => !o)}
            className="admin-profile-btn"
          >
            <div className="admin-profile-avatar">
              {(user?.username || user?.email || "A")[0].toUpperCase()}
            </div>
            <span className="admin-profile-name">
              {user?.username || user?.email || "Admin"}
            </span>
            <motion.span
              animate={{ rotate: profileOpen ? 180 : 0 }}
              transition={{ duration: 0.2 }}
              style={{ display: "flex" }}
            >
              <ChevronDown size={14} />
            </motion.span>
          </motion.button>

          <AnimatePresence>
            {profileOpen && (
              <motion.div
                key="profile-dropdown"
                initial={{ opacity: 0, y: -8, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.97 }}
                transition={{ duration: 0.16, ease: [0.4, 0, 0.2, 1] }}
                className="admin-profile-dropdown"
              >
                <div className="admin-profile-dropdown-header">
                  <div className="admin-profile-dropdown-avatar">
                    {(user?.username || user?.email || "A")[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="admin-profile-dropdown-name">
                      {user?.username || user?.email || "Admin"}
                    </p>
                    <p className="admin-profile-dropdown-role">Administrator</p>
                  </div>
                </div>
                <div className="admin-profile-dropdown-divider" />
                <button
                  className="admin-profile-dropdown-item admin-profile-dropdown-item--danger"
                  onClick={() => {
                    setProfileOpen(false);
                    onLogout();
                  }}
                >
                  <LogOut size={14} />
                  <span>Logout</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
}
